import clsx from 'clsx';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@doger/ui/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@doger/ui/components/ui/dialog';
import { Link } from '@doger/ui/components/ui/link';

import {
  type CookiePreferences,
  CookieSetting,
  DEFAULT_PREFERENCE,
  useCookiePreferences,
} from '../../shared/cookies';
import styles from './cookie-banner.module.scss';

type CookieBannerProps = {
  className?: string;
  onPreferences: (preferences: CookiePreferences) => void;
};

export const CookieBanner = ({
  className,
  onPreferences,
}: CookieBannerProps) => {
  const { settings, savePreferences, updateAnalytics, updateMarketing } =
    useCookiePreferences({
      onAlreadySet: onPreferences,
    });
  const { t } = useTranslation('core');

  const [isVisible, setVisible] = useState(() => settings === null);

  const handleAcceptAll = () => {
    savePreferences({
      acceptAll: true,
      settings: {
        analytics: true,
        marketing: true,
      },
    });
    setVisible(false);
  };

  const handleSaveSettings = () => {
    savePreferences(
      settings ?? {
        acceptAll: DEFAULT_PREFERENCE,
        settings: {
          analytics: DEFAULT_PREFERENCE,
          marketing: DEFAULT_PREFERENCE,
        },
      }
    );
    setVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={clsx(styles.banner, className)}>
      <div className={styles.banner__content}>
        <div className={styles.banner__text}>
          <h3 className={styles.banner__title}>{t('cookies.banner.title')}</h3>

          <p className={styles.banner__description}>
            <Trans
              i18nKey='cookies.banner.description'
              ns='core'
              components={{
                cookiePolicy: (
                  <Link
                    className={styles.banner__link}
                    to='/cookie-policy'
                    target='_blank'
                  />
                ),
                privacyPolicy: (
                  <Link
                    className={styles.banner__link}
                    to='/privacy-policy'
                    target='_blank'
                  />
                ),
              }}
            />
          </p>
        </div>
        <div className={styles.banner__actions}>
          <Button
            variant='contained'
            size='lg'
            palette='primary'
            onClick={handleAcceptAll}
            className={styles.banner__button}
          >
            {t('cookies.banner.acceptAll')}
          </Button>

          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant='contained'
                size='lg'
                palette='ghost'
                className={styles.banner__button}
              >
                {t('cookies.banner.manageSettings')}
              </Button>
            </DialogTrigger>
            <DialogContent className={styles.dialog}>
              <DialogHeader>
                <DialogTitle>{t('cookies.manage.title')}</DialogTitle>

                <DialogDescription>
                  {t('cookies.manage.description')}
                </DialogDescription>
              </DialogHeader>

              <DialogBody className={styles.dialog__content}>
                <div className={styles.dialog__description}>
                  {t('cookies.manage.necessaryDescription')}
                </div>
                <div className={styles.dialog__description}>
                  {t('cookies.manage.optionalDescription')}
                </div>

                <div className={styles.dialog__settings}>
                  <CookieSetting
                    label={t('cookies.manage.analyticsLabel')}
                    description={t('cookies.manage.analyticsDescription')}
                    checked={settings?.settings.analytics ?? DEFAULT_PREFERENCE}
                    onChange={updateAnalytics}
                  />
                  <CookieSetting
                    label={t('cookies.manage.marketingLabel')}
                    description={t('cookies.manage.marketingDescription')}
                    checked={
                      settings?.settings?.marketing ?? DEFAULT_PREFERENCE
                    }
                    onChange={updateMarketing}
                  />
                </div>
              </DialogBody>

              <DialogFooter className={styles.dialog__footer}>
                <DialogClose asChild>
                  <Button variant='outlined' size='lg' palette='ghost'>
                    {t('cookies.manage.cancel')}
                  </Button>
                </DialogClose>
                <Button
                  variant='contained'
                  size='lg'
                  palette='primary'
                  onClick={handleSaveSettings}
                >
                  {t('cookies.manage.saveSettings')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};
